import { cloneDeep, isEqual } from 'lodash';

import type { WidgetDTO } from '@neptune/shared/core-apis-leaderboard-domain';
import {
  type AttributeWidgetSource,
  isAttributeSource,
  type Widget,
  type WidgetDefinitionNew,
} from '@neptune/shared/widgets-domain';

import { WidgetDefinition } from 'domain/widget/core/domain/widget-definition';

import { getVegaLiteFloatSeriesManifestIssues } from './vega-lite-float-series-compiler';
import { cloneJsonObject, isJsonObject, type JsonObject } from './vega-lite-json';
import {
  createDefaultVegaLiteWidgetOptions,
  decodeVegaLiteWidgetOptions,
  getVegaLiteWidgetOptionsIssues,
  isVegaLiteWidgetOptions,
  type VegaLiteWidgetOptions,
} from './vega-lite-widget-options';

export const VEGA_LITE_WIDGET_DEFINITION_METADATA: WidgetDefinitionNew = {
  widgetType: 'vegaLite',
  label: 'Vega-Lite chart',
  description: 'Define evaluation or float-series data and a Vega-Lite chart in JSON.',
  icon: 'chart-line',
  dataItem: 'vegaLite',
  hasSettings: true,
  template: {
    type: 'vegaLite',
    options: createDefaultVegaLiteWidgetOptions(),
    sources: [],
  },
};

export type VegaLiteWidget = PersistedVegaLiteWidget & {
  type: 'vegaLite';
  options: VegaLiteWidgetOptions;
};

/**
 * Options as they are persisted. Payloads written by a newer client keep their original shape so an
 * unrelated save cannot drop fields this client does not understand yet.
 */
export type PersistedVegaLiteWidgetOptions = VegaLiteWidgetOptions | JsonObject;

type PersistedVegaLiteConfiguration = {
  type: string;
  options?: unknown;
  sources: readonly unknown[];
  opaqueVegaLiteApiConfiguration?: {
    widget: WidgetDTO;
    sourcesJson: string;
  };
};

export type PersistedVegaLiteWidget = Widget &
  Pick<PersistedVegaLiteConfiguration, 'opaqueVegaLiteApiConfiguration'>;

export function getOpaqueVegaLiteApiWidget(
  widget: PersistedVegaLiteConfiguration,
): WidgetDTO | undefined {
  const original = widget.opaqueVegaLiteApiConfiguration;

  // Content checks survive immutable/JSON copies without allowing authored changes to bypass validation.
  if (
    widget.type === 'vegaLite' &&
    original &&
    isEqual(widget.options, toWidgetOptionsValue(original.widget.options)) &&
    JSON.stringify(widget.sources) === original.sourcesJson
  ) {
    return original.widget;
  }

  return undefined;
}

/**
 * Saved options this client cannot read as a bounded JSON object: a tree over the JSON budget, or a
 * value that is not an object. Such a widget stays opaque and read-only instead of aborting the
 * dashboard or report that contains it.
 */
export function isUnreadablePersistedVegaLiteOptions(options: unknown): boolean {
  return options !== undefined && !isJsonObject(options);
}

/** `Widget.options` can hold only an object; other saved values are restored from the original. */
function toWidgetOptionsValue(options: unknown): object | undefined {
  return typeof options === 'object' && options !== null ? options : undefined;
}

/** Export paths cannot carry the API original, so they reject it instead of writing a default. */
export function assertReadablePersistedVegaLiteOptions(
  widget: PersistedVegaLiteConfiguration,
): void {
  if (isUnreadablePersistedVegaLiteOptions(getOpaqueVegaLiteApiWidget(widget)?.options)) {
    throw new Error('Saved Vega-Lite options cannot be read; no default was substituted.');
  }
}

export function isVegaLiteWidget(widget: Widget): widget is VegaLiteWidget {
  if (
    widget.type !== 'vegaLite' ||
    getOpaqueVegaLiteApiWidget(widget) ||
    !isVegaLiteWidgetOptions(widget.options)
  ) {
    return false;
  }

  return (
    widget.options.version !== 2 ||
    (getVegaLiteWidgetOptionsIssues(widget.options).length === 0 &&
      getVegaLiteFloatSeriesManifestIssues(widget.options, widget.sources).length === 0)
  );
}

export function getVegaLiteEvaluationSource(widget: Widget): AttributeWidgetSource | undefined {
  if (widget.sources.length !== 1) {
    return undefined;
  }

  const [source] = widget.sources;

  if (
    !isAttributeSource(source) ||
    source.metadata.attributeType !== 'evaluationSeries' ||
    source.metadata.subproperty !== undefined
  ) {
    return undefined;
  }

  return source;
}

export function hasVegaLiteEvaluationSource(widget: Widget): boolean {
  return getVegaLiteEvaluationSource(widget) !== undefined;
}

export function assertVegaLiteFloatSeriesConfiguration(
  widget: PersistedVegaLiteConfiguration,
): void {
  if (widget.type !== 'vegaLite' || !isJsonObject(widget.options) || widget.options.version !== 2) {
    return;
  }

  const decoded = decodeVegaLiteWidgetOptions(widget.options);

  if (decoded.status === 'invalid') {
    throw new Error(decoded.errors.join(' '));
  }

  if (decoded.status === 'supported' && decoded.options.version === 2) {
    const issues = [
      ...getVegaLiteWidgetOptionsIssues(decoded.options),
      ...getVegaLiteFloatSeriesManifestIssues(decoded.options, widget.sources),
    ];

    if (issues.length) {
      throw new Error(issues.map((issue) => `${issue.path}: ${issue.message}`).join(' '));
    }

    if (getOpaqueVegaLiteApiWidget(widget)) {
      throw new Error('Unsupported saved Vega-Lite source manifest.');
    }
  }
}

export function convertPersistedVegaLiteWidgetOptions(
  options?: unknown,
): PersistedVegaLiteWidgetOptions {
  if (options === undefined) {
    return createDefaultVegaLiteWidgetOptions();
  }

  const decoded = decodeVegaLiteWidgetOptions(options);

  if (decoded.status === 'supported') {
    return decoded.options;
  }

  if (!isJsonObject(options)) {
    throw new Error(
      'Persisted Vega-Lite options must be a JSON object; no default was substituted.',
    );
  }

  return cloneJsonObject(options);
}

export function vegaLiteWidgetOptionsFromApiToDomain(
  options?: Record<string, unknown>,
): PersistedVegaLiteWidgetOptions | object | undefined {
  if (isJsonObject(options) && options.version === 2) {
    // Options alone cannot validate the dependency manifest; keep the saved payload intact.
    return cloneJsonObject(options);
  }

  if (isUnreadablePersistedVegaLiteOptions(options)) {
    // Never substitute a default here: the widget converter keeps the API original, so an
    // unchanged widget is written back exactly.
    return toWidgetOptionsValue(cloneDeep(options));
  }

  return convertPersistedVegaLiteWidgetOptions(options);
}

export function vegaLiteWidgetOptionsFromDomainToApi(options?: unknown): object {
  return convertPersistedVegaLiteWidgetOptions(options);
}

export const vegaLiteWidgetDefinition: WidgetDefinition = {
  type: 'vegaLite',
  fromApiToDomain: vegaLiteWidgetOptionsFromApiToDomain,
  fromDomainToApi: vegaLiteWidgetOptionsFromDomainToApi,
};
